import {
  AGGREGATED_SEARCH_QUERY,
  AGGREGATED_SEARCH_QUERY_FAIL,
  AGGREGATED_SEARCH_QUERY_NEXT_PAGE,
  AGGREGATED_SEARCH_QUERY_NEXT_PAGE_FAIL,
  AGGREGATED_SEARCH_QUERY_NEXT_PAGE_SUCCESS,
  AGGREGATED_SEARCH_QUERY_SUCCESS,
  CHANNELS_SEARCH_QUERY,
  INBOX_SEARCH_PARAMS_UPDATE,
  LIBRARY_SELECT,
  PANE_LOAD,
  PLAYLIST_DELETE_BATCH_SUCCESS,
  PRELOAD_DATA_ON_LOGIN,
  SEARCH_ADD_METADATA_FILTER,
  SEARCH_CLEAR,
  SEARCH_HISTORY_APPEND,
  SEARCH_PARAMS_UPDATE,
  SEARCH_QUERY,
  SEARCH_QUERY_FAIL,
  SEARCH_QUERY_SUCCESS,
  SEARCH_REMOVE_METADATA_FILTER,
  SEARCH_RESULTS_CACHE_SET,
  SHORTLISTS_TOGGLE,
  TRACK_DELETE_BATCH_SUCCESS,
  TRACK_MERGE_SUCCESS,
  RECIPIENT_SEARCH_PARAMS_UPDATE,
} from '@disco/constants/ActionTypes'
import { ARTIST, PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import { normalizeSearchTerm } from '@disco/utils/search'
import type { SearchFilter } from '@disco/utils/search'
import type { SORT_DIRECTION } from '@disco/constants/SortTypes'
import { SORT_DIRECTION_DESC } from '@disco/constants/SortTypes'
import type { ExplorerPane, SortFields } from 'types/explorer'
import type { FilterState } from 'types/filter-page'
import type { V2SearchQuery } from 'types/search-page'
import type { SearchType } from '@disco/constants/search'

import { BROWSE_CONTENT_CHANGES } from './browse-content-changes'

type SearchListItem =
  | { id: number; type: string }
  | { playlist_id: number; type: string }

export type AppliedSearch = {
  searchTerm: string
  groups: number[]
  groupObjects: Array<{
    id: number
    group_type: string
    name?: string
    internal_name?: string
    [key: string]: unknown
  }>
  tags: string[]
  nativeTags: number[]
  filters: SearchFilter[]
  startDate: Date | null
  endDate: Date | null
  duration: string | null
  filterState: FilterState[] | null
}

const initialState = {
  searchOpen: false,
  searchHeight: null,
  searchCompact: true,
  searchTerm: '',
  searchQuery: null,
  searchQueryHistory: [] as Array<{
    title: string
    type: 'searchQuery'
  }>,
  searching: false,
  error: false,
  list: null as Array<SearchListItem> | null,
  type: false as false | SearchType,
  nextPage: false,
  // Total matching results reported by the last successful search; undefined
  // on the legacy path, which reports no total.
  total: undefined as number | undefined,
  sort: 'score',
  sortDirection: SORT_DIRECTION_DESC,
  metadataKeys: [] as Array<any>,
  filters: [] as Array<any>,
  groups: [] as Array<number>,
  tags: [] as Array<string>,
  facet: [] as Array<any>,
  nativeTags: [] as Array<number>,
  // nativeTags as of the last executed search query — nativeTags itself can
  // hold pending values the user set in the filters panel without searching
  searchedNativeTags: [] as Array<number>,
  startDate: new Date(0) as Date,
  endDate: null,
  duration: null,
  // Parameters used by the last executed search. The fields above also back
  // the advanced-filter form and may contain changes that are not applied yet.
  appliedSearch: null as AppliedSearch | null,
  aggregatedSearch: {
    isSearching: false,
    isLoadingNextPage: false,
    searchResults: [] as Array<any>,
    // v2 pagination cursor for the next aggregated page
    nextPageUrl: null,
    // mds-scoped V2SearchQuery used to fetch further pages
    queryV2: null,
    error: false,
  },
  channelsSearch: {
    searchTerm: '',
  },
  inboxSearch: {
    searchTerm: '',
    searchOpen: false,
    isSearching: false,
    type: false as false | 'track' | 'playlist' | 'inbox',
  },
  recipientSearch: {
    searchTerm: '',
    searchOpen: false,
  },
  // STL-1248: first page of each result type for the current Browse search, so
  // switching back to a type skips the request.
  resultsCache: {
    searchKey: null as string | null,
    byType: {} as Record<string, CachedSearchResults>,
  },
}

/** First page of one result type, as needed to rebuild the Browse search pane. */
export type CachedSearchResults = {
  list?: Array<SearchListItem>
  nextPage?: string | null
  total?: number
  facet?: Array<unknown>
  searchQuery?: Record<string, unknown>
  pane?: Partial<ExplorerPane>
  aggregatedSearch?: {
    searchResults: Array<unknown>
    nextPageUrl: string | null
    queryV2: V2SearchQuery
  }
}
export type { SimSearchQuery } from 'types/search-page'
export type SearchFilters = {
  searchTerm?: string
  type: false | SearchType
  groups: number[]
  tags: string[]
  nativeTags: number[]
  sort: SortFields | string
  sortDirection: SORT_DIRECTION
  startDate: Date | null | undefined
  duration: string | null | undefined
  filters: Record<string, any>[]
}
// TODO: add detailed types for all fields in search state
export type State = typeof initialState & SearchFilters

function normalizeSearchResults(list) {
  return list
    .map(item => {
      if (item.type === TRACK || item.type === ARTIST) {
        return {
          id: item.id,
          type: item.type,
        }
      }

      if (item.type === PLAYLIST) {
        return {
          playlist_id: item.playlist_id,
          type: item.type,
        }
      }
    })
    .filter(Boolean)
}

const getDefaultMetadataFilter = metadataKeys => {
  const keyObj = metadataKeys.length > 0 ? metadataKeys[0] : ''
  return {
    key: typeof keyObj === 'string' ? keyObj : keyObj.key,
    value: '',
    type: '',
    start: '',
    end: '',
  }
}

const areMetadataFiltersSet = filters => {
  if (!filters || filters.length === 0) return false
  const nonEmptyFilterPresent = filters.find(
    f => f.value !== '' || f.start !== '' || f.end !== '',
  )
  return nonEmptyFilterPresent
}

function searchStateReducer(
  state: State = initialState,
  action: Record<string, any> = {},
): State {
  const { params, type, list, nextPage } = action

  switch (type) {
    case SEARCH_CLEAR:
      return {
        ...initialState,
        searchTerm: action.keepQuery ? state.searchTerm : '',
        searchOpen: true,
        searchCompact: state.searchCompact,
        facet: [],
        nativeTags: action.keepQuery ? state.nativeTags : [],
        searchedNativeTags: action.keepQuery ? state.searchedNativeTags : [],
        metadataKeys: state.metadataKeys,
        sort: state.sort,
        sortDirection: state.sortDirection,
        filters: [getDefaultMetadataFilter(state.metadataKeys)],
      }

    case LIBRARY_SELECT:
      if (!action.library) return state
      // Selecting a library (Browse) must fully reset the search state so a
      // stale term/filters/results can't resurface later. Preserve user
      // preferences, loaded config, and unrelated sub-searches.
      return {
        ...initialState,
        searchCompact: state.searchCompact,
        sort: state.sort,
        sortDirection: state.sortDirection,
        metadataKeys: state.metadataKeys,
        searchQueryHistory: state.searchQueryHistory,
        filters: [getDefaultMetadataFilter(state.metadataKeys)],
        channelsSearch: state.channelsSearch,
        inboxSearch: state.inboxSearch,
        recipientSearch: state.recipientSearch,
        searchOpen: false,
        searchHeight: null,
      }

    case PANE_LOAD:
      return { ...state, searchCompact: true }

    case PRELOAD_DATA_ON_LOGIN:
      if (action.entities && action.entities.metadataKeys) {
        return {
          ...state,
          metadataKeys: action.entities.metadataKeys,
          filters: [getDefaultMetadataFilter(action.entities.metadataKeys)],
        }
      }

      return state

    case SEARCH_PARAMS_UPDATE: {
      // Store invariant: searchTerm is always normalized (trimmed, single
      // spaces), so no reader can ever see a whitespace-only-but-truthy term
      const normalizedParams =
        typeof params.searchTerm === 'string'
          ? { ...params, searchTerm: normalizeSearchTerm(params.searchTerm) }
          : params
      const result = { ...state, ...normalizedParams }

      // Metadata filters and track tags only apply to tracks, so applying one
      // from All switches to Tracks rather than stranding a disabled tab.
      const newlyTrackOnly =
        (!areMetadataFiltersSet(state.filters) &&
          areMetadataFiltersSet(params.filters)) ||
        (params.nativeTags !== undefined &&
          state.nativeTags.length === 0 &&
          params.nativeTags.length > 0)

      if (newlyTrackOnly && params.type === undefined && state.type === false) {
        result.type = TRACK
      }

      return result
    }

    case SEARCH_QUERY:
      return {
        ...state,
        searching: true,
        facet: [],
        searchedNativeTags:
          action.appliedSearch?.nativeTags ?? state.nativeTags,
        appliedSearch: action.appliedSearch ?? state.appliedSearch,
        total: undefined,

        list: [],
        // Only a result type tab keeps what the same search already loaded;
        // running the search again means the user wants fresh results
        resultsCache:
          action.keepResultsCache &&
          action.resultsCacheKey &&
          action.resultsCacheKey === state.resultsCache.searchKey
            ? state.resultsCache
            : {
                searchKey: action.resultsCacheKey ?? null,
                byType: {},
              },
      }

    case SEARCH_RESULTS_CACHE_SET: {
      // A late response from a search the user already replaced
      if (action.searchKey !== state.resultsCache.searchKey) return state
      return {
        ...state,
        resultsCache: {
          ...state.resultsCache,
          byType: {
            ...state.resultsCache.byType,
            [action.resultType]: {
              ...state.resultsCache.byType[action.resultType],
              ...action.entry,
            },
          },
        },
      }
    }

    case SEARCH_ADD_METADATA_FILTER:
      return { ...state, filters: [...state.filters, action.filter] }

    case SEARCH_REMOVE_METADATA_FILTER:
      if (action.filterIndex === 0 && state.filters.length === 1) {
        return {
          ...state,
          filters: [getDefaultMetadataFilter(state.metadataKeys)],
        }
      }

      return {
        ...state,
        filters: [
          ...state.filters.slice(0, action.filterIndex),
          ...state.filters.slice(action.filterIndex + 1),
        ],
      }

    case SEARCH_QUERY_SUCCESS:
      return {
        ...state,
        searching: false,
        searchQuery: action.searchQuery,
        facet: action.facet,
        list: normalizeSearchResults(list),
        nextPage: nextPage,
        total: action.total,
      }

    case SEARCH_HISTORY_APPEND: {
      const { searchTerm } = state

      if (!searchTerm) {
        return state
      }

      let nextSearchQueryHistory = [...state.searchQueryHistory]
      const existingHistoryIndex = nextSearchQueryHistory.findIndex(
        searchQuery => searchQuery.title === searchTerm,
      )

      if (existingHistoryIndex === -1) {
        nextSearchQueryHistory = [
          {
            title: searchTerm,
            type: 'searchQuery',
          },
          ...state.searchQueryHistory,
        ]
      } else {
        // existingHistoryIndex was found by findIndex so it is in bounds
        const searchQueryItem = nextSearchQueryHistory[existingHistoryIndex]!
        nextSearchQueryHistory.splice(existingHistoryIndex, 1)
        nextSearchQueryHistory.splice(0, 0, searchQueryItem)
      }

      nextSearchQueryHistory = nextSearchQueryHistory.slice(0, 3)
      return { ...state, searchQueryHistory: nextSearchQueryHistory }
    }
    case SEARCH_QUERY_FAIL:
      return { ...state, searching: false, error: true, total: undefined }

    case AGGREGATED_SEARCH_QUERY:
      return {
        ...state,
        aggregatedSearch: {
          ...initialState.aggregatedSearch,
          isSearching: true,
        },
      }

    case AGGREGATED_SEARCH_QUERY_SUCCESS:
      return {
        ...state,
        aggregatedSearch: {
          isSearching: false,
          isLoadingNextPage: false,
          searchResults: params.aggregatedResults,
          nextPageUrl: params.aggregatedNextPageUrl,
          queryV2: params.aggregatedQueryV2 ?? null,
          error: false,
        },
      }

    case AGGREGATED_SEARCH_QUERY_FAIL:
      return {
        ...state,
        aggregatedSearch: {
          searchResults: [],
          nextPageUrl: null,
          queryV2: null,
          isSearching: false,
          isLoadingNextPage: false,
          error: true,
        },
      }

    case AGGREGATED_SEARCH_QUERY_NEXT_PAGE:
      return {
        ...state,
        aggregatedSearch: {
          ...state.aggregatedSearch,
          isLoadingNextPage: true,
        },
      }

    case AGGREGATED_SEARCH_QUERY_NEXT_PAGE_SUCCESS:
      return {
        ...state,
        aggregatedSearch: {
          isSearching: false,
          isLoadingNextPage: false,
          searchResults: [
            ...state.aggregatedSearch.searchResults,
            ...params.aggregatedResults,
          ],
          nextPageUrl: params.aggregatedNextPageUrl,
          queryV2: state.aggregatedSearch.queryV2,
          error: false,
        },
      }

    case AGGREGATED_SEARCH_QUERY_NEXT_PAGE_FAIL:
      return {
        ...state,
        aggregatedSearch: {
          ...state.aggregatedSearch,
          isLoadingNextPage: false,
          nextPageUrl: null,
        },
      }

    case TRACK_DELETE_BATCH_SUCCESS:
      return {
        ...state,
        list: state.list
          ? state.list.filter(
              item =>
                item.type !== TRACK ||
                !('id' in item) ||
                action.trackIds.indexOf(item.id) === -1,
            )
          : null,
      }

    case TRACK_MERGE_SUCCESS: {
      const trackIds = action.tracksToMerge.map(t => t.id)
      return {
        ...state,
        list: state.list
          ? state.list.filter(
              item =>
                item.type !== TRACK ||
                !('id' in item) ||
                trackIds.indexOf(item.id) === -1,
            )
          : null,
      }
    }
    case PLAYLIST_DELETE_BATCH_SUCCESS:
      return {
        ...state,
        list: state.list
          ? state.list.filter(
              item =>
                item.type !== PLAYLIST ||
                !('playlist_id' in item) ||
                action.playlistIds.indexOf(item.playlist_id) === -1,
            )
          : null,
      }

    case SHORTLISTS_TOGGLE:
      return {
        ...state,
        searchOpen: false,
        searchHeight: null,
        searchCompact: false,
      }

    case CHANNELS_SEARCH_QUERY:
      return {
        ...state,
        channelsSearch: {
          searchTerm: action.channelsSearchTerm,
        },
      }

    case INBOX_SEARCH_PARAMS_UPDATE:
      return {
        ...state,
        inboxSearch: { ...state.inboxSearch, ...action.params },
      }
    case RECIPIENT_SEARCH_PARAMS_UPDATE:
      return {
        ...state,
        recipientSearch: { ...state.recipientSearch, ...action.params },
      }

    default:
      return state
  }
}

/** Search slice reducer; drops the Browse results cache when Browse contents change. */
export function searchReducer(
  state: State = initialState,
  action: Record<string, any> = {},
): State {
  const next = searchStateReducer(state, action)
  if (
    !BROWSE_CONTENT_CHANGES.includes(action.type) ||
    next.resultsCache === initialState.resultsCache
  )
    return next
  return { ...next, resultsCache: initialState.resultsCache }
}
